import {
  AssistantCitation,
  AssistantPart,
  AssistantSessionRow,
} from '@services/modules/AssistantStream'
import appStore from '@stores/appStore'
import { observer } from 'mobx-react'
import { KeyboardEvent, ReactNode } from 'react'

import { AssistantDateOptions, renderAssistantDates } from '../assistantDates'
import { assistantZoneLabel } from '../assistantZoneLabel'
import { canOpenCitation, openCitation } from '../citationNavigation'
import { SESSION_CITATION } from '../sessionCitations'

/*
 * The sessions a calendar search listed, drawn from the rows the server sent - never from the
 * answer's text. Each time opens its own session by its id, also when two rows show the same time
 * in the same city; specialists and centres open their screen for a user who may open it. It is
 * drawn inside the answer's markdown block, so Copy takes it with the answer.
 */

export const SESSION_TABLE_PART = 'session_table'

/** A record in a cell: a link when the user may open it, bold when they may not, else its name. */
const RecordCell = ({
  citation,
  children,
}: {
  citation?: AssistantCitation
  children: ReactNode
}) => {
  if (!citation) {
    return <>{children}</>
  }
  if (citation.openable === false) {
    return <strong>{children}</strong>
  }
  if (!canOpenCitation(citation)) {
    return <>{children}</>
  }
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      openCitation(citation)
    }
  }
  return (
    <span
      role="link"
      tabIndex={0}
      className="kw-cite"
      title="Open the record"
      onClick={() => openCitation(citation)}
      onKeyDown={onKeyDown}
    >
      {children}
    </span>
  )
}

/** The citation a row's time opens: its session, on its own day and in its own zone. */
export const sessionCitation = (
  row: AssistantSessionRow,
  label: string
): AssistantCitation => ({
  type: SESSION_CITATION,
  id: row.sessionId,
  label,
  start: row.start,
  timeZone: row.timeZone,
})

const statusText = (row: AssistantSessionRow) =>
  row.booked ? 'Booked' : row.reserved ? 'Reserved' : 'Available'

/** A row's start as the app's lists show an appointment: in its own zone, with that zone named. */
const startText = (row: AssistantSessionRow, options: AssistantDateOptions) =>
  row.start
    ? renderAssistantDates(
        row.timeZone
          ? `{{datetime:${row.start}|${row.timeZone}}}`
          : `{{datetime:${row.start}}}`,
        options
      )
    : ''

/** A list of free slots needs no status; one that mixes booked and free ones does. */
const showsStatus = (rows: AssistantSessionRow[]) =>
  rows.some(row => row.booked || row.reserved) && rows.some(row => !row.booked)

const COLUMNS = ['Session', 'Specialist', 'Centre', 'Type']

/** The table as markdown text, for the plain-text flavour of Copy. */
export const sessionTableText = (
  part: AssistantPart,
  options: AssistantDateOptions
): string => {
  const rows = part.sessions ?? []
  if (rows.length === 0) {
    return ''
  }
  const status = showsStatus(rows)
  const cell = (value?: string) => (value ?? '').replace(/\|/g, '\\|')
  const line = (cells: string[]) => `| ${cells.join(' | ')} |`
  const header = status ? [...COLUMNS, 'Status'] : COLUMNS
  return [
    line(header),
    line(header.map(() => '---')),
    ...rows.map(row =>
      line([
        cell(startText(row, options)),
        cell(row.specialist?.label),
        cell(row.centre?.label),
        row.telehealth ? 'Telehealth' : 'In person',
        ...(status ? [statusText(row)] : []),
      ])
    ),
  ].join('\n')
}

const SessionTablePart = ({ part }: { part: AssistantPart }) => {
  const rows = part.sessions ?? []
  if (rows.length === 0) {
    return null
  }

  const dateOptions = {
    dateFormat: appStore.currentUser?.dateFormat,
    defaultTimeZone: appStore.timezone,
    zoneLabel: assistantZoneLabel,
  }
  const showStatus = showsStatus(rows)

  return (
    <div>
      <table>
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <th key={column}>{column}</th>
            ))}
            {showStatus && <th>Status</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const start = startText(row, dateOptions)
            return (
              <tr key={row.sessionId}>
                <td>
                  <RecordCell
                    citation={start ? sessionCitation(row, start) : undefined}
                  >
                    {start}
                  </RecordCell>
                </td>
                <td>
                  <RecordCell citation={row.specialist}>
                    {row.specialist?.label}
                  </RecordCell>
                </td>
                <td>
                  <RecordCell citation={row.centre}>
                    {row.centre?.label}
                  </RecordCell>
                </td>
                <td>{row.telehealth ? 'Telehealth' : 'In person'}</td>
                {showStatus && <td>{statusText(row)}</td>}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default observer(SessionTablePart)
